import React, { useMemo } from "react";

const DOT_CYCLE = ["present", "leave", "absent", "half", "wo"];
const DOT_CLASS = {
  present: "bg-[hsl(var(--hrms-present))]",
  absent: "bg-[hsl(var(--hrms-absent))]",
  leave: "bg-[hsl(var(--hrms-leave))]",
  half: "bg-[hsl(var(--hrms-half))]",
  wo: "bg-[hsl(var(--hrms-wo))]",
};

function ActionRailButton({ action, selected, onSelect, tone = "present" }) {
  return (
    <button
      type="button"
      onClick={() => onSelect?.(action.key)}
      aria-current={selected ? "true" : undefined}
      className={`flex h-10 w-full shrink-0 items-center gap-2.5 rounded-[11px] px-2.5 text-left transition-colors ${
        selected
          ? "bg-[hsl(var(--ds-primary)/0.12)] shadow-[inset_3px_0_0_hsl(var(--ds-primary))]"
          : "bg-transparent hover:bg-[hsl(var(--hrms-ink)/0.04)]"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT_CLASS[tone] || DOT_CLASS.wo}`}
        aria-hidden
      />
      <span
        className={`min-w-0 flex-1 truncate text-[13px] font-semibold leading-none tracking-tight ${
          selected ? "text-[hsl(var(--ds-primary))]" : "text-[hsl(var(--hrms-ink))]"
        }`}
      >
        {action.label}
      </span>
    </button>
  );
}

/**
 * Shared Actions shell — identical chrome for Attendance / Payroll / Employee.
 * Fixed-height rows, status dots, redesign tokens (no product-theme tint).
 */
export default function ModuleActionsShell({
  crumbModule = "Module",
  crumbLeaf = "Actions",
  title = "Actions",
  subtitle = "",
  groups = null,
  actions = [],
  activeKey,
  onSelect,
  children,
}) {
  const byKey = useMemo(() => {
    const map = new Map();
    (actions || []).forEach((a) => map.set(a.key, a));
    return map;
  }, [actions]);

  const resolvedGroups = useMemo(() => {
    if (Array.isArray(groups) && groups.length > 0) {
      return groups
        .map((g) => ({
          ...g,
          items: (g.keys || []).map((k) => byKey.get(k)).filter(Boolean),
        }))
        .filter((g) => g.items.length > 0);
    }
    return [
      {
        id: "all",
        label: "Actions",
        items: actions || [],
      },
    ];
  }, [groups, byKey, actions]);

  let flatIndex = 0;

  return (
    <div className="module-actions-shell flex min-h-0 flex-1 overflow-hidden rounded-[18px] border border-[hsl(var(--hrms-line))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-panel)]">
      <aside className="flex w-[17.5rem] shrink-0 flex-col border-r border-[hsl(var(--hrms-line))] bg-[hsl(var(--ds-surface))]">
        <div className="shrink-0 border-b border-[hsl(var(--hrms-line))] px-4 py-3.5">
          <p className="text-caption text-[hsl(var(--hrms-mute))]">
            {crumbModule}{" "}
            <span className="text-[hsl(var(--hrms-line))]">/</span>{" "}
            <span className="font-semibold text-[hsl(var(--ds-primary))]">{crumbLeaf}</span>
          </p>
          <h2 className="text-title mt-1 text-[hsl(var(--hrms-ink))]">{title}</h2>
          {subtitle ? (
            <p className="text-caption mt-0.5 text-[hsl(var(--hrms-mute))]">{subtitle}</p>
          ) : null}
        </div>
        <nav
          className="admin-shell-scrollbar min-h-0 flex-1 overflow-y-auto px-2 py-2"
          aria-label={title}
        >
          {resolvedGroups.map((g) => (
            <div key={g.id} className="mb-1">
              {resolvedGroups.length > 1 || g.label !== "Actions" ? (
                <p className="px-2.5 pb-1 pt-2.5 text-[11px] font-semibold text-[hsl(var(--hrms-mute))]">
                  {g.label}
                </p>
              ) : (
                <p className="px-2.5 pb-1 pt-2.5 text-[11px] font-semibold text-[hsl(var(--hrms-mute))]">
                  {g.label}
                </p>
              )}
              <ul className="space-y-0.5">
                {g.items.map((a) => {
                  const tone = DOT_CYCLE[flatIndex % DOT_CYCLE.length];
                  flatIndex += 1;
                  return (
                    <li key={a.key} className="h-10">
                      <ActionRailButton
                        action={a}
                        selected={a.key === activeKey}
                        onSelect={onSelect}
                        tone={tone}
                      />
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
      <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[hsl(var(--ds-surface))] p-3 sm:p-4">
        {children}
      </section>
    </div>
  );
}
